import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/Button.tsx';
import { Disclosure } from '../../components/Disclosure.tsx';
import { ReadOnlyNote } from '../../components/ReadOnlyNote.tsx';
import { StateLabel, TruthLabel } from '../../components/TruthLabel.tsx';
import { formatDateTime } from '../../components/format.ts';
import { buttonClass } from '../../components/buttonClass.ts';
import { TimelineEvent } from '../../components/TimelineEvent.tsx';
import { WorkspaceTerminal } from '../../features/terminal/WorkspaceTerminal.tsx';
import {
  api,
  ApiError,
  type ContainerView,
  type TerminalCapability,
} from '../../features/onboarding/api.ts';
import { attemptStatusLabel, isAttemptActive } from '../../features/discovery/model.ts';
import { useDiscovery } from '../../features/discovery/store.ts';
import { DnsSummaryCard } from '../../features/discovery/DnsSummaryCard.tsx';
import { ScanTimer } from '../../features/discovery/ScanTimer.tsx';
import {
  findingId,
  findingsCountText,
  isProgressFinding,
  isSecurityFinding,
} from '../../features/discovery/evidenceSummary.ts';
import { SEVERITIES } from '../../features/findings/model.ts';
import { useWorkspaceFindings } from '../../features/findings/store.ts';
import {
  coverageCountText,
  discoveryActivityLog,
  discoveryPipelineStep,
  discoveryStatusText,
  formatDiscoveryClock,
  releasedCapabilityLabels,
} from '../../features/discovery/activityLog.ts';
import { useActivityHistory } from '../../features/discovery/activityHistory.ts';
import { useCan } from '../../features/auth/permissions.ts';
import { mandateStatus, readOwner } from '../../features/authorization/model.ts';
import { authorizedScopeSummary } from '../../features/authorization/scope.ts';
import { useSession } from '../../state/session.ts';
import { overviewTimelineEvents, type OverviewTimelineEvent } from './overviewTimeline.ts';
import styles from './OverviewPage.module.css';

function RecentChangesList({
  events,
  limit,
  selectedId,
  onSelect,
}: {
  events: OverviewTimelineEvent[];
  limit: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const visibleEvents = events.slice(0, limit);

  if (visibleEvents.length === 0) {
    return (
      <div className={styles.emptyRecentChanges}>
        <p className={styles.instrumentText}>No recent changes recorded.</p>
      </div>
    );
  }

  return (
    <div className={styles.recentChangesList} role="list" aria-label="Recent changes">
      {visibleEvents.map((event) => (
        <TimelineEvent
          key={event.id}
          {...event}
          isSelected={selectedId === event.id}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

// Fresh workspace scenario (FRONTEND_TASKS.md §4; DOMAIN_AUTHORIZATION_SPEC.md §6.3; WORKSPACE_COCKPIT_SPEC.md §9.2):
// workspace overview: discovery availability, verified-domain context, container track.
const sentence = (value?: string) => value && value.charAt(0).toUpperCase() + value.slice(1);

export function OverviewPage() {
  const workspace = useSession((s) => s.workspace);
  const justOnboarded = useSession((s) => s.justOnboarded);
  const clearEntrance = useSession((s) => s.clearEntrance);
  const setSessionContainer = useSession((s) => s.setContainer);
  const canWrite = useCan('workspace.write');
  const heading = useRef<HTMLHeadingElement>(null);
  // Captured once: the entrance plays only on arrival from onboarding.
  const [entering] = useState(justOnboarded);
  const [selectedRecentChange, setSelectedRecentChange] = useState<string | null>(null);

  const [container, setContainer] = useState<ContainerView | null>(workspace?.container ?? null);
  // Only the live container GET is authoritative for the terminal (never stored views).
  const [terminal, setTerminal] = useState<TerminalCapability | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!justOnboarded) return;
    heading.current?.focus();
    clearEntrance();
  }, [justOnboarded, clearEntrance]);

  const pollRef = useRef<(delayMs?: number) => void>(() => {});

  const workspaceId = workspace?.id;
  const ownerToken = workspace?.authorizationId ? readOwner(workspace.authorizationId) : '';
  const setupSessionId = workspace?.setupSessionId ?? '';
  // A signed-in actor's token takes precedence; otherwise the setup or owner capability.
  const readToken = ownerToken;
  const discoveryCredentials = useMemo(
    () => ({ session: setupSessionId, token: readToken }),
    [setupSessionId, readToken],
  );
  const eligibility = useDiscovery((s) => s.eligibility);
  const records = useDiscovery((s) => s.records);
  const recentChanges = overviewTimelineEvents(container, records);
  const progress = useDiscovery((s) => s.progress);
  const eligibilityLoading = useDiscovery((s) => s.eligibilityLoading);
  const recordsLoading = useDiscovery((s) => s.recordsLoading);
  const recordsLoaded = useDiscovery((s) => s.recordsLoaded);
  const eligibilityStale = useDiscovery((s) => s.eligibilityStale);
  const recordsStale = useDiscovery((s) => s.recordsStale);
  const eligibilityError = useDiscovery((s) => s.eligibilityError);
  const recordsError = useDiscovery((s) => s.recordsError);
  const pendingStart = useDiscovery((s) => s.pendingStart);
  const startResponse = useDiscovery((s) => s.startResponse);
  const startPending = useDiscovery((s) => s.startPending);
  const startRetryAvailable = useDiscovery((s) => s.startRetryAvailable);
  const startError = useDiscovery((s) => s.startError);
  const stopPending = useDiscovery((s) => s.stopPending);
  const stopAcknowledgement = useDiscovery((s) => s.stopAcknowledgement);
  const stopError = useDiscovery((s) => s.stopError);
  const loadDiscovery = useDiscovery((s) => s.load);
  const loadLatestAttempt = useDiscovery((s) => s.loadLatestAttempt);
  const loadProgress = useDiscovery((s) => s.loadProgress);
  const startDiscovery = useDiscovery((s) => s.startDiscovery);
  const stopDiscovery = useDiscovery((s) => s.stopDiscovery);
  const persistedFindings = useWorkspaceFindings((s) => s.findings);
  const loadWorkspaceFindings = useWorkspaceFindings((s) => s.loadLatestAttemptFindings);
  const poll = useCallback(
    (delayMs = 0) => {
      if (!workspaceId || cancelledRef.current) return;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      pollTimerRef.current = setTimeout(async () => {
        if (cancelledRef.current || !workspaceId) return;
        try {
          const view = await api.getWorkspaceContainer(workspaceId, setupSessionId, readToken);
          if (cancelledRef.current) return;
          setContainer(view);
          setSessionContainer(view, workspaceId);
          setTerminal(view.terminal ?? null);
          if (view.pollAfterMs !== null) {
            pollRef.current(view.pollAfterMs);
          } else if (
            view.phase === 'running' &&
            (!view.terminal?.available || view.terminal.state !== 'none')
          ) {
            // Wait for the security check, or for another tab's terminal to finish.
            pollRef.current(10000);
          }
        } catch (error) {
          if (cancelledRef.current) return;
          if (error instanceof ApiError && error.status === 429) {
            pollRef.current((error.retryAfterSeconds ?? 10) * 1000);
          } else {
            pollRef.current(10000);
          }
        }
      }, delayMs);
    },
    [workspaceId, setupSessionId, readToken, setSessionContainer],
  );

  useEffect(() => {
    pollRef.current = poll;
  }, [poll]);

  useEffect(() => {
    cancelledRef.current = false;
    poll(0);
    return () => {
      cancelledRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [poll]);

  // Discovery GETs are safe on entry; mutation actions are only called from explicit controls.
  useEffect(() => {
    if (!workspaceId) return;
    void loadDiscovery(workspaceId, discoveryCredentials);
    void loadWorkspaceFindings(workspaceId, discoveryCredentials);
  }, [workspaceId, discoveryCredentials, loadDiscovery, loadWorkspaceFindings]);

  const attempt = records?.attempt ?? null;
  const stopAcknowledgementMatchesAttempt = Boolean(
    stopAcknowledgement &&
    (attempt?.attemptId === stopAcknowledgement.attemptId ||
      startResponse?.attemptId === stopAcknowledgement.attemptId),
  );
  const attemptStatus =
    stopAcknowledgementMatchesAttempt && stopAcknowledgement?.status === 'STOPPED'
      ? stopAcknowledgement.status
      : (attempt?.status ?? startResponse?.status ?? null);
  const activeAttempt =
    attemptStatus !== 'STOPPED' &&
    (isAttemptActive(attempt) ||
      (!attempt &&
        (attemptStatus === 'QUEUED' ||
          attemptStatus === 'RUNNING' ||
          attemptStatus === 'STOP_REQUESTED')));
  const stopReceipt =
    attempt?.stop ?? (stopAcknowledgementMatchesAttempt ? stopAcknowledgement?.stop : undefined);
  const cleanupReceipt =
    attempt?.cleanup ??
    (stopAcknowledgementMatchesAttempt ? stopAcknowledgement?.cleanup : undefined);

  useEffect(() => {
    if (!workspaceId || !activeAttempt || recordsLoading) return;
    const timer = setTimeout(() => {
      void Promise.all([
        loadLatestAttempt(workspaceId, discoveryCredentials),
        loadWorkspaceFindings(workspaceId, discoveryCredentials),
      ]);
    }, 4000);
    return () => clearTimeout(timer);
  }, [
    workspaceId,
    activeAttempt,
    recordsLoading,
    attemptStatus,
    discoveryCredentials,
    loadLatestAttempt,
    loadWorkspaceFindings,
  ]);

  // Progress is one light request, so it polls faster: unit lines reach the terminal while the
  // scan runs. Records + findings (4s) and this (1.5s) stay well under the 120/min rate limit.
  useEffect(() => {
    if (!workspaceId || !activeAttempt) return;
    const timer = setInterval(() => void loadProgress(workspaceId, discoveryCredentials), 1500);
    return () => clearInterval(timer);
  }, [workspaceId, activeAttempt, discoveryCredentials, loadProgress]);

  // Progress (1.5s) and the attempt record (4s) learn that a scan ended at different times. When
  // the record has it first, read progress for the minutes saved with it; when progress has it
  // first, read the record, so the status and the last run's minutes change together.
  const progressActive =
    progress?.status === 'QUEUED' ||
    progress?.status === 'RUNNING' ||
    progress?.status === 'STOP_REQUESTED';
  const sameAttempt = Boolean(attempt && progress?.attemptId === attempt.attemptId);
  const progressBehind = sameAttempt && !activeAttempt && progressActive;
  const recordBehind = sameAttempt && activeAttempt && !progressActive;
  useEffect(() => {
    if (workspaceId && progressBehind) void loadProgress(workspaceId, discoveryCredentials);
  }, [workspaceId, progressBehind, discoveryCredentials, loadProgress]);
  useEffect(() => {
    if (workspaceId && recordBehind) void loadLatestAttempt(workspaceId, discoveryCredentials);
  }, [workspaceId, recordBehind, discoveryCredentials, loadLatestAttempt]);

  const inspectorRecords = useMemo(() => {
    if (!records) return records;
    if (persistedFindings.length === 0 && !(progress?.findings ?? []).length) return records;
    const byId = new Map(records.findings.map((finding) => [findingId(finding), finding]));
    for (const finding of persistedFindings) {
      byId.set(findingId(finding), finding);
    }
    for (const projected of (progress?.findings ?? []).filter(isProgressFinding)) {
      const existing = byId.get(findingId(projected));
      if (!existing) continue;
      byId.set(findingId(projected), {
        ...existing,
        lane: existing.lane ?? projected.lane,
        normalizedSeverity: existing.normalizedSeverity ?? projected.normalizedSeverity,
      });
    }
    return { ...records, findings: [...byId.values()] };
  }, [records, persistedFindings, progress]);

  // The decision queue: findings still waiting for a person, most severe first (§4.1 region 3).
  const stopFlow =
    stopAcknowledgementMatchesAttempt ||
    attemptStatus === 'STOP_REQUESTED' ||
    attemptStatus === 'STOPPED';

  const needsReview = useMemo(
    () =>
      persistedFindings
        .filter(
          (finding) =>
            finding.analystReviewState === 'UNREVIEWED' ||
            finding.analystReviewState === 'NEEDS_EVIDENCE',
        )
        .sort(
          (a, b) =>
            SEVERITIES.indexOf(a.normalizedSeverity) - SEVERITIES.indexOf(b.normalizedSeverity),
        ),
    [persistedFindings],
  );

  const polledActivity = useMemo(
    () => discoveryActivityLog(progress, inspectorRecords),
    [progress, inspectorRecords],
  );
  // Keep what the runner reported, across scans, so status lines read in order instead of
  // staying pinned last and an earlier scan stays above the next one.
  const runnerActivity = useActivityHistory(polledActivity, workspaceId ?? '');
  const runnerPipelineStep = useMemo(
    () => discoveryPipelineStep(progress, inspectorRecords),
    [progress, inspectorRecords],
  );
  const runnerStatusText = discoveryStatusText(
    attemptStatus,
    runnerPipelineStep,
    progress?.summary,
  );
  const runnerStatus = useMemo(
    () =>
      runnerStatusText ? { label: runnerStatusText, live: attemptStatus === 'RUNNING' } : null,
    [runnerStatusText, attemptStatus],
  );

  const terminalActive = terminal?.state === 'connecting' || terminal?.state === 'connected';

  const discoveryStatusLabel = startPending
    ? 'Start request pending'
    : startRetryAvailable || pendingStart
      ? 'Start response not confirmed'
      : attemptStatus === 'STOPPED'
        ? 'Stopped'
        : stopAcknowledgementMatchesAttempt || attemptStatus === 'STOP_REQUESTED'
          ? 'Stop requested'
          : (attemptStatusLabel(attemptStatus) ??
            (eligibilityLoading || (recordsLoading && !recordsLoaded)
              ? 'Checking discovery state'
              : eligibility?.eligible && eligibility.executionAllowed
                ? 'Ready to start'
                : 'Not started / unavailable'));
  const canStartDiscovery = Boolean(
    workspaceId &&
    eligibility?.eligible &&
    eligibility.executionAllowed &&
    !terminalActive &&
    recordsLoaded &&
    !recordsStale &&
    !recordsError &&
    !eligibilityLoading &&
    !eligibilityStale &&
    !activeAttempt &&
    !startPending,
  );
  const canRetryStart = Boolean(
    (startRetryAvailable || pendingStart) &&
    eligibility?.eligible &&
    eligibility.executionAllowed &&
    !terminalActive &&
    recordsLoaded &&
    !recordsStale &&
    !recordsError &&
    !eligibilityStale &&
    !activeAttempt &&
    !startPending,
  );
  const startInput = eligibility
    ? {
        profileId: eligibility.profileSummary.profileId,
        profileVersion: eligibility.profileSummary.profileVersion,
      }
    : null;
  const runtimeReadiness =
    sentence(eligibility?.containerStatus.state.toLowerCase().replaceAll('_', ' ')) ??
    'Not reported';

  const scanStatusTone =
    attemptStatus === 'RUNNING'
      ? 'running'
      : attemptStatus === 'STOPPED' || attemptStatus === 'FAILED'
        ? 'stopped'
        : startPending || attemptStatus === 'QUEUED' || attemptStatus === 'STOP_REQUESTED'
          ? 'pending'
          : eligibility?.eligible && eligibility.executionAllowed
            ? 'ready'
            : 'idle';

  if (!workspace) return null;

  const mandate = mandateStatus(workspace.mandate) ?? 'active';

  return (
    <div className={styles.page} data-entering={entering || undefined}>
      <header className={styles.headingRow} aria-label="Workspace orientation">
        <div className={styles.header}>
          <h1 ref={heading} tabIndex={-1} className={styles.title}>
            Overview
          </h1>
        </div>
        <nav className={styles.findingsActions} aria-label="Overview actions">
          <Link to="/findings" className={buttonClass('secondary', 'small')}>
            View findings
          </Link>
        </nav>
      </header>

      <div className={styles.terminalRow}>
        {/* Terminal-first cockpit (DESIGN_SYSTEM.md §4.1): the terminal leads in DOM and visual order. */}
        <div className={styles.terminalSlot}>
          {workspace.authorizationId && (
            <WorkspaceTerminal
              workspaceId={workspace.id}
              workspaceName={workspace.name}
              domain={workspace.domain}
              signedScopeDomains={
                eligibility?.authority ? eligibility.scopeSummary.scopeDomains : undefined
              }
              phase={container?.phase ?? 'none'}
              container={container}
              authorizerName={workspace.authorizer?.name}
              authorizationConfirmedAt={workspace.authorizationConfirmedAt}
              capability={terminal}
              authorizationId={workspace.authorizationId}
              setupSessionId={setupSessionId}
              ownerToken={ownerToken}
              onEnded={() => poll(0)}
              activityEntries={runnerActivity}
              pipelineStep={runnerPipelineStep}
              activityStatus={runnerStatus}
              readOnly={!canWrite}
            />
          )}
        </div>

        <section className={styles.componentsContainer} aria-labelledby="workspace-context-title">
          <h2 id="workspace-context-title" className="sr-only">
            Workspace
          </h2>

          <div className={styles.workspaceLinePrimary}>
            <dl className={styles.workspaceMetaDl} aria-label="Workspace metadata">
              <div className={styles.metaItem}>
                <dt className={styles.metaLabel}>Authorization</dt>
                <dd className={styles.metaValue}>
                  <span>{sentence(mandate.replace('_', ' '))}</span>
                  <Link to="/authorization" className={styles.textLink}>
                    View authorization
                  </Link>
                </dd>
              </div>
            </dl>
          </div>

          <div
            id="discovery-scan-status"
            className={styles.discoveryBar}
            role="region"
            aria-label="Scan status"
          >
            <div className={styles.discoveryHeadline}>
              <span className={styles.discoveryEyebrow}>Discovery</span>
              <span className={styles.discoveryStatusRow}>
                <span className={styles.scanStatusValue} role="status" data-status={scanStatusTone}>
                  {discoveryStatusLabel}
                </span>
                <ScanTimer progress={progress} attemptId={attempt?.attemptId} />
              </span>
            </div>
            <div className={styles.discoveryMainRow}>
              <div className={styles.discoveryActionsGroup}>
                {canWrite ? (
                  <>
                    <Button
                      variant="primary"
                      size="small"
                      isDisabled={!(canStartDiscovery || canRetryStart)}
                      onPress={() => {
                        if (workspaceId && startInput) {
                          void startDiscovery(workspaceId, startInput, discoveryCredentials).then(
                            () => {
                              void loadWorkspaceFindings(workspaceId, discoveryCredentials);
                            },
                          );
                        }
                      }}
                      aria-describedby="discovery-unavailable-reason"
                    >
                      {startPending
                        ? 'Starting…'
                        : startRetryAvailable || pendingStart
                          ? 'Retry Start'
                          : 'Start discovery'}
                    </Button>
                    {activeAttempt && (
                      <Button
                        variant="secondary"
                        size="small"
                        isDisabled={stopPending}
                        onPress={() => {
                          if (workspaceId) {
                            void stopDiscovery(workspaceId, discoveryCredentials).then(() => {
                              void loadWorkspaceFindings(workspaceId, discoveryCredentials);
                            });
                          }
                        }}
                      >
                        {stopPending
                          ? 'Requesting stop…'
                          : stopAcknowledgementMatchesAttempt || attemptStatus === 'STOP_REQUESTED'
                            ? 'Request stop again'
                            : 'Stop discovery'}
                      </Button>
                    )}
                  </>
                ) : (
                  <ReadOnlyNote>Viewers can’t start discovery or use the terminal.</ReadOnlyNote>
                )}
                {(eligibilityError || recordsError) && (
                  <Button
                    variant="tertiary"
                    size="small"
                    isDisabled={eligibilityLoading || recordsLoading}
                    onPress={() => {
                      if (!workspaceId) return;
                      void loadDiscovery(workspaceId, discoveryCredentials);
                      void loadWorkspaceFindings(workspaceId, discoveryCredentials);
                    }}
                  >
                    Refresh discovery state
                  </Button>
                )}
              </div>
            </div>

            <div id="discovery-unavailable-reason" className={styles.disabledReason}>
              {eligibility?.reasons && eligibility.reasons.length > 0 ? (
                <ul className={styles.reasonList} aria-label="Discovery eligibility blockers">
                  {eligibility.reasons.map((r) => (
                    <li key={r.code} className={styles.reasonItem} data-code={r.code}>
                      <span className={styles.reasonMessage}>{r.message}</span>
                    </li>
                  ))}
                </ul>
              ) : eligibilityError ? (
                <span>{eligibilityError.message}</span>
              ) : eligibility && !eligibility.executionAllowed ? (
                <span>Discovery execution is disabled in this environment.</span>
              ) : eligibility && !eligibility.eligible ? (
                <span>Current workspace eligibility does not allow a discovery request.</span>
              ) : terminalActive ? (
                <span>
                  A terminal session is active on this workspace container. Disconnect the terminal
                  before running discovery.
                </span>
              ) : pendingStart ? (
                <span>
                  The last Start response was not confirmed. Retry sends the same saved request with
                  its original idempotency key.
                </span>
              ) : activeAttempt ? (
                <span>An active discovery attempt already exists.</span>
              ) : startInput ? null : (
                <span>Network discovery availability has not been confirmed.</span>
              )}
            </div>

            <div className={styles.discoveryMetaGroup}>
              <dl className={styles.discoveryMetaDl} aria-label="Discovery scope and profile">
                <div className={styles.discoveryMetaItem}>
                  <dt className={styles.discoveryMetaLabel}>Scope</dt>
                  <dd className={styles.discoveryMetaValue}>
                    {authorizedScopeSummary(
                      eligibility?.scopeSummary,
                      'No authorized domain scope returned',
                    )}
                  </dd>
                </div>
                <div className={styles.discoveryMetaItem}>
                  <dt className={styles.discoveryMetaLabel}>Profile</dt>
                  <dd className={styles.discoveryMetaValue}>
                    {eligibility?.profileSummary?.profileName ?? 'Network and DNS discovery'}
                  </dd>
                </div>
                <div className={styles.discoveryMetaItem}>
                  <dt className={styles.discoveryMetaLabel}>Runtime readiness</dt>
                  <dd className={styles.discoveryMetaValue}>{runtimeReadiness}</dd>
                </div>
                {(attempt ||
                  (inspectorRecords?.findings.length ?? 0) > 0 ||
                  (progress?.findings ?? []).some(isProgressFinding)) && (
                  <div className={styles.discoveryMetaItem}>
                    <dt className={styles.discoveryMetaLabel}>Findings</dt>
                    <dd className={styles.discoveryMetaValue}>
                      {findingsCountText(
                        [
                          ...(inspectorRecords?.findings ?? []),
                          ...(progress?.findings ?? []).filter(
                            (finding) =>
                              isProgressFinding(finding) &&
                              !(inspectorRecords?.findings ?? []).some(
                                (item) => findingId(item) === findingId(finding),
                              ),
                          ),
                        ].filter(isSecurityFinding),
                        inspectorRecords?.evidence ?? [],
                      )}
                    </dd>
                  </div>
                )}
              </dl>
              {(stopAcknowledgementMatchesAttempt ||
                attemptStatus === 'STOP_REQUESTED' ||
                attemptStatus === 'STOPPED') && (
                <dl className={styles.discoveryDetailsDl} aria-label="Stop and cleanup receipts">
                  {/* While a stop is pending, the observed state stays beside the request (§10.9). */}
                  <div className={styles.discoveryMetaItem}>
                    <dt className={styles.discoveryMetaLabel}>Observed attempt</dt>
                    <dd className={styles.discoveryMetaValue}>
                      {attemptStatusLabel(attemptStatus) ?? 'No attempt recorded'}
                    </dd>
                  </div>
                  <>
                    <div className={styles.discoveryMetaItem}>
                      <dt className={styles.discoveryMetaLabel}>Stop request</dt>
                      <dd className={styles.discoveryMetaValue}>
                        {stopReceipt ? (
                          <>
                            Requested by {stopReceipt.requestedBy?.displayName ?? 'Workspace owner'}
                            {' · '}
                            <time dateTime={stopReceipt.requestedAt}>
                              {stopReceipt.requestedAt}
                            </time>
                          </>
                        ) : stopAcknowledgementMatchesAttempt ||
                          attemptStatus === 'STOP_REQUESTED' ? (
                          'Stop request receipt unavailable from the current record.'
                        ) : (
                          'No Stop request receipt recorded.'
                        )}
                      </dd>
                    </div>
                    <div className={styles.discoveryMetaItem}>
                      <dt className={styles.discoveryMetaLabel}>Observed stop</dt>
                      <dd className={styles.discoveryMetaValue}>
                        {attemptStatus === 'STOPPED'
                          ? `Stopped${attempt?.finishedAt ? ` · ${attempt.finishedAt}` : ''}`
                          : attemptStatus === 'STOP_REQUESTED' || stopAcknowledgementMatchesAttempt
                            ? 'Not confirmed yet'
                            : 'Not recorded'}
                      </dd>
                    </div>
                    <div className={styles.discoveryMetaItem}>
                      <dt className={styles.discoveryMetaLabel}>Cleanup</dt>
                      <dd className={styles.discoveryMetaValue}>
                        {cleanupReceipt
                          ? [
                              cleanupReceipt.state === 'NOT_REQUIRED'
                                ? 'Not required'
                                : cleanupReceipt.state === 'CONFIRMED'
                                  ? 'Confirmed'
                                  : cleanupReceipt.state === 'FAILED'
                                    ? 'Failed'
                                    : 'Pending',
                              cleanupReceipt.confirmedAt,
                              cleanupReceipt.receiptId
                                ? `receipt ${cleanupReceipt.receiptId}`
                                : undefined,
                              cleanupReceipt.details,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : 'No cleanup receipt recorded.'}
                      </dd>
                    </div>
                  </>
                </dl>
              )}
              <Disclosure title="Run details" bare>
                <dl
                  className={styles.discoveryDetailsDl}
                  aria-label="Discovery runtime and attempt state"
                >
                  <div className={styles.discoveryMetaItem}>
                    <dt className={styles.discoveryMetaLabel}>Released capabilities</dt>
                    <dd className={styles.discoveryMetaValue}>
                      {releasedCapabilityLabels(eligibility?.profileSummary?.supportedMethods)}
                    </dd>
                  </div>
                  {!stopFlow && (
                    <div className={styles.discoveryMetaItem}>
                      <dt className={styles.discoveryMetaLabel}>Observed attempt</dt>
                      <dd className={styles.discoveryMetaValue}>
                        {attemptStatusLabel(attemptStatus) ?? 'No attempt recorded'}
                      </dd>
                    </div>
                  )}
                  {progress ? (
                    <>
                      <div className={styles.discoveryMetaItem}>
                        <dt className={styles.discoveryMetaLabel}>Runner heartbeat</dt>
                        <dd className={styles.discoveryMetaValue}>
                          {progress.heartbeatState === 'NOT_STARTED'
                            ? 'Not started'
                            : progress.heartbeatState === 'HEALTHY'
                              ? 'Healthy'
                              : progress.heartbeatState === 'STALE'
                                ? 'Stale'
                                : 'Finished'}
                        </dd>
                      </div>
                      <div className={styles.discoveryMetaItem}>
                        <dt className={styles.discoveryMetaLabel}>Last useful progress</dt>
                        <dd className={styles.discoveryMetaValue}>
                          {progress.lastUsefulProgress ? (
                            <time dateTime={progress.lastUsefulProgress}>
                              {formatDiscoveryClock(progress.lastUsefulProgress)}
                            </time>
                          ) : (
                            'No useful progress recorded'
                          )}
                        </dd>
                      </div>
                      <div className={styles.discoveryMetaItem}>
                        <dt className={styles.discoveryMetaLabel}>Coverage</dt>
                        <dd className={styles.discoveryMetaValue}>
                          {coverageCountText(progress.summary)}
                        </dd>
                      </div>
                    </>
                  ) : null}
                  {(attempt || startResponse) && (
                    <div className={styles.discoveryMetaItem}>
                      <dt className={styles.discoveryMetaLabel}>Started by</dt>
                      <dd className={styles.discoveryMetaValue}>
                        {attempt?.start?.requestedBy?.displayName ??
                          startResponse?.requestedBy?.displayName ??
                          'Workspace owner'}
                        {' · '}
                        <time dateTime={attempt?.start?.requestedAt ?? startResponse?.requestedAt}>
                          {formatDateTime(
                            attempt?.start?.requestedAt ?? startResponse?.requestedAt,
                          )}
                        </time>
                      </dd>
                    </div>
                  )}
                </dl>
              </Disclosure>
            </div>

            {startPending && (
              <p className={styles.instrumentText} aria-live="polite">
                Waiting for Traceira to confirm the Start request.
              </p>
            )}
            {startError && (
              <p role="alert" className={styles.problem}>
                {startError.message}
                {startError.code === 'IDEMPOTENCY_PAYLOAD_MISMATCH' &&
                  ' The saved command key belongs to a different request; review the current scope and retry only after confirming the latest attempt.'}
                {startError.recoveryAction ? ` ${startError.recoveryAction}` : ''}
              </p>
            )}
            {startRetryAvailable && (
              <p className={styles.instrumentText}>
                Retry reuses the same idempotency key because the server may have accepted the
                original request.
              </p>
            )}
            {recordsError && (
              <p role={recordsStale ? 'status' : 'alert'} className={styles.disabledReason}>
                {recordsStale ? 'Showing the last confirmed attempt. ' : ''}
                {recordsError.message}
              </p>
            )}
            {stopAcknowledgementMatchesAttempt && stopAcknowledgement && (
              <p className={styles.instrumentText} role="status">
                {stopAcknowledgement.message}
              </p>
            )}
            {stopError && (
              <p role="alert" className={styles.problem}>
                {stopError.message}
              </p>
            )}
          </div>
        </section>

        {/* Inside the terminal row so narrow screens can place it right under the terminal. */}
        <section className={styles.needsReview} aria-labelledby="needs-attention-title">
          <div className={styles.instrumentHeader}>
            <h2 id="needs-attention-title" className={styles.instrumentTitle}>
              Needs your review
            </h2>
            {persistedFindings.length > 0 && (
              <Link to="/findings" className={styles.textLink}>
                All {persistedFindings.length} findings
              </Link>
            )}
          </div>
          {needsReview.length === 0 ? (
            <p className={styles.instrumentText}>Nothing needs your review right now.</p>
          ) : (
            <ul className={styles.attentionList}>
              {needsReview.slice(0, 5).map((finding) => (
                <li key={finding.findingId}>
                  <Link
                    to="/findings/$findingId"
                    params={{ findingId: finding.findingId }}
                    className={styles.attentionRow}
                  >
                    <StateLabel kind="severity" state={finding.normalizedSeverity} compact />
                    <span className={styles.attentionTitle}>{finding.title}</span>
                    <TruthLabel
                      dimension="review"
                      state={finding.analystReviewState}
                      compact
                      bare
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section
          className={`${styles.recentChangesCard} ${styles.recentChanges}`}
          aria-labelledby="recent-changes-title"
          data-testid="overview-wide-recent-changes"
        >
          <div className={styles.instrumentHeader}>
            <h2 id="recent-changes-title" className={styles.instrumentTitle}>
              Recent changes
            </h2>
          </div>
          <RecentChangesList
            events={recentChanges}
            limit={5}
            selectedId={selectedRecentChange}
            onSelect={setSelectedRecentChange}
          />
        </section>
      </div>

      {(records?.attempt || activeAttempt) && (
        <Disclosure title="Discovery results" defaultExpanded={activeAttempt}>
          <DnsSummaryCard
            records={inspectorRecords}
            progress={progress}
            domain={workspace.domain}
            isActive={activeAttempt}
          />
        </Disclosure>
      )}
    </div>
  );
}
